/**
 * One card on a trade, the same on both sides: the picture, what it is, its quantity and price, and
 * the buttons to move it to the other side or take it off. A customer line also shows the market
 * value its offer comes from, and any flags on how it was priced.
 */
import { useState } from 'react'
import { AlertTriangle, ArrowLeftRight, Lock, Minus, Package, Pencil, Plus, Trash2 } from 'lucide-react'
import { hasCardNumber } from '../../../utils/cardNumber'
import { readLineMarketCents, readLinePricing, type LinePriceUpdate } from './customerPricing'
import { TradeLinePriceEditor } from './TradeLinePriceEditor'
import { TradeLineVariantEditor, type LineVariant } from './TradeLineVariantEditor'
import type { TradeItem } from './types'
import styles from '../CreateTransaction.module.css'

type Props = {
  item: TradeItem
  imageSrc: string | null
  onImageError: () => void
  formatPrice: (cents: number) => string
  globalCustomerPercent: number | ''
  bulkFloorUsd?: Parameters<typeof TradeLinePriceEditor>[0]['bulkFloorUsd']
  priceDisabled: boolean
  onSavePrice: (update: LinePriceUpdate) => Promise<boolean>
  onPriceEditing: (editing: boolean) => void
  onDecrease: () => void
  onIncrease: () => void
  atMax: boolean
  onMove: () => void
  moveDisabled: boolean
  onRemove: () => void
  /** Just arrived from a phone: highlighted for a moment. */
  fresh?: boolean
  /** Text for the "Check printing" flag, when there's one. */
  printingNote?: string | null
  /** Correct the printing or condition; resolves false when it failed. */
  onChangeVariant: (variant: LineVariant) => Promise<boolean>
}

export function TradeLineRow({
  item, imageSrc, onImageError, formatPrice, globalCustomerPercent, bulkFloorUsd, priceDisabled, onSavePrice, onPriceEditing,
  onDecrease, onIncrease, atMax, onMove, moveDisabled, onRemove, fresh, printingNote, onChangeVariant,
}: Props) {
  const [editing, setEditing] = useState(false)
  const customer = item.side === 'customer'
  const pricing = readLinePricing(item.metadata)
  const market = customer ? readLineMarketCents(item.metadata) : 0
  const percent = pricing.percentOfMarket
  const otherSide = customer ? "store's" : "customer's"
  return (
    <div className={`${styles.tradeItem} ${fresh ? styles.tradeItemFresh : ''}`}>
      <div className={`${styles.itemImageWrapper} ${imageSrc ? styles.hasImage : ''}`}>
        {imageSrc && <img src={imageSrc} alt={item.itemName} width={56} height={78} loading="lazy" decoding="async" onError={onImageError} />}
        <Package size={16} className={styles.placeholderIcon} aria-hidden />
      </div>
      <div className={styles.itemInfo}>
        <span className={styles.itemName}>
          {item.itemName}{hasCardNumber(item.cardNumber) && !item.itemName.includes(item.cardNumber) ? ` #${item.cardNumber}` : ''}
        </span>
        <span className={styles.itemMeta}>
          {item.setName} •{' '}
          {item.cardId ? (
            <button type="button" className={styles.itemVariantBtn} aria-expanded={editing} aria-label={`Change printing or condition of ${item.itemName}: ${item.printing}, ${item.condition}`} onClick={() => setEditing((v) => !v)}>
              {item.printing} {item.condition} <Pencil size={11} aria-hidden />
            </button>
          ) : <>{item.printing} {item.condition}</>}
          {customer && pricing.floorApplied === true && (
            <span className={styles.tradePercentBadge} title="Bulk floor: under the store's bulk threshold, so valued at the flat bulk amount">floor</span>
          )}
          {customer && pricing.manualOverride === true && (
            <span className={styles.tradePercentBadge} title="Custom price — the Customer % of Market won't change it" aria-label="Custom price">
              <Lock size={10} aria-hidden />
            </span>
          )}
        </span>
        {(market > 0 || printingNote) && (
          <span className={styles.itemMarket}>
            {market > 0 && (
              <span>
                Market {formatPrice(market * item.quantity)}
                {typeof percent === 'number' && pricing.floorApplied !== true && pricing.manualOverride !== true && ` · ${Math.round(percent * 10) / 10}%`}
              </span>
            )}
            {printingNote && (
              <button type="button" className={styles.printingFlag} title={printingNote} aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
                <AlertTriangle size={12} aria-hidden /> Check printing
              </button>
            )}
          </span>
        )}
      </div>
      <div className={styles.itemQuantity}>
        <button type="button" aria-label={`Decrease quantity for ${item.itemName}`} onClick={onDecrease}>
          <Minus size={14} aria-hidden />
        </button>
        <span>{item.quantity}</span>
        <button type="button" aria-label={`Increase quantity for ${item.itemName}`} onClick={onIncrease} disabled={atMax}>
          <Plus size={14} aria-hidden />
        </button>
      </div>
      <TradeLinePriceEditor
        item={item}
        formatPrice={formatPrice}
        globalCustomerPercent={globalCustomerPercent}
        bulkFloorUsd={bulkFloorUsd}
        disabled={priceDisabled}
        onSave={onSavePrice}
        onEditingChange={onPriceEditing}
      />
      <button type="button" className={styles.swapBtn} aria-label={`Move ${item.itemName} to the ${otherSide} cards`} title={`Move to the ${otherSide} cards`} disabled={moveDisabled} onClick={onMove}>
        <ArrowLeftRight size={14} aria-hidden />
      </button>
      <button type="button" className={styles.removeBtn} aria-label={`Remove ${item.itemName} from ${customer ? 'customer' : 'store'} items`} onClick={onRemove}>
        <Trash2 size={14} aria-hidden />
      </button>
      {editing && item.cardId && (
        <TradeLineVariantEditor cardId={item.cardId} side={customer ? 'customer' : 'store'} printing={item.printing} condition={item.condition}
          note={printingNote} name={item.itemName} onSave={onChangeVariant} onCancel={() => setEditing(false)} />
      )}
    </div>
  )
}
